/**
 * FASE 4.2 — Area Cliente Unificata.
 * RegisterModal: registrazione self-service (no invito admin necessario).
 *
 * Flow:
 *  1. Form anagrafico minimo (email, password, telefono, CF, consensi)
 *  2. POST /api/client/register → status=pending_otp
 *  3. Step OTP: POST /api/client/otp/verify (purpose=registration)
 *  4. Auto-login + chiusura modal
 */
import { useState } from "react";
import { X, Mail, Lock, Loader2, ArrowRight, Phone, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useClientAuth } from "@/contexts/ClientAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;

export default function RegisterModal({ open, onClose, onSwitchToLogin }) {
  const { login: setSession } = useClientAuth();
  const [step, setStep] = useState("form"); // form | otp
  const [form, setForm] = useState({
    email: "", password: "", phone_e164: "", cf: "",
    privacy: false, marketing: false,
  });
  const [otpCode, setOtpCode] = useState("");
  const [debugCode, setDebugCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const close = () => {
    setStep("form"); setForm({ email: "", password: "", phone_e164: "", cf: "", privacy: false, marketing: false });
    setOtpCode(""); setError(null); setDebugCode(null); onClose();
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.privacy) { setError("Accetta l'informativa privacy per continuare"); return; }
    setLoading(true); setError(null);
    try {
      const phone = form.phone_e164.startsWith("+") ? form.phone_e164 : `+39${form.phone_e164}`;
      const { data } = await axios.post(`${API}/register`, {
        email: form.email.toLowerCase(),
        password: form.password,
        phone_e164: phone,
        cf: form.cf || undefined,
        consents: { privacy: form.privacy, marketing: form.marketing },
      });
      if (data?.status === "pending_otp") {
        setDebugCode(data?.debug_code || null);
        setStep("otp");
        toast.success("OTP inviato via SMS");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || JSON.stringify(detail) || "Registrazione fallita"));
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, {
        email: form.email.toLowerCase(),
        code: otpCode,
        purpose: "registration",
      });
      if (data?.token) {
        setSession(data.token, data.account || null);
        toast.success("Benvenuto/a! Account creato.");
        close();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || "OTP non valido"));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={close} data-testid="register-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="register-modal">
        <button onClick={close} className="absolute right-3 top-3 p-1.5 rounded-full hover:bg-zinc-100"
          data-testid="register-modal-close" aria-label="Chiudi">
          <X className="w-4 h-4 text-zinc-500" />
        </button>

        <header className="mb-4">
          <h2 className="text-2xl font-semibold text-[#163321]">Registrati</h2>
          <p className="text-sm text-zinc-500 mt-1">
            {step === "form" ? "Crea il tuo account Area Cliente Colucci" :
              "Inserisci il codice OTP ricevuto via SMS"}
          </p>
        </header>

        {step === "form" && (
          <form onSubmit={submitForm} className="space-y-3" data-testid="register-form">
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Email *</span>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value.toLowerCase())} required
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm" data-testid="register-email" />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Telefono cellulare *</span>
              <div className="relative mt-1">
                <Phone className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="tel" value={form.phone_e164} onChange={(e) => setField("phone_e164", e.target.value)} required
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm" placeholder="+39 333 1234567"
                  data-testid="register-phone" />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">Riceverai un codice OTP via SMS</p>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Codice Fiscale (opzionale, per auto-link al tuo CRM)</span>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="text" value={form.cf} onChange={(e) => setField("cf", e.target.value.toUpperCase())}
                  maxLength={16}
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono"
                  data-testid="register-cf" />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Password * (min 12 caratteri)</span>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
                <input type="password" value={form.password} onChange={(e) => setField("password", e.target.value)} required minLength={12}
                  className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm"
                  data-testid="register-password" />
              </div>
            </label>
            <label className="flex items-start gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={form.privacy} onChange={(e) => setField("privacy", e.target.checked)}
                className="mt-0.5" data-testid="register-privacy" />
              <span>Ho letto e accetto l'<a href="/privacy" target="_blank" className="text-emerald-700 underline">informativa privacy</a> *</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={form.marketing} onChange={(e) => setField("marketing", e.target.checked)}
                className="mt-0.5" data-testid="register-marketing" />
              <span>Accetto le comunicazioni di marketing (facoltativo)</span>
            </label>
            {error && <p className="text-xs text-rose-600" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={loading || !form.email || !form.password || !form.phone_e164 || !form.privacy}
              className="w-full bg-[#163321] text-white font-semibold py-2.5 rounded-lg hover:bg-[#1d4a2e] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="register-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Continua
            </button>
            <p className="text-xs text-center text-zinc-500 pt-2 border-t border-zinc-100">
              Hai già un account?{" "}
              <button type="button" onClick={() => { onSwitchToLogin?.(); close(); }}
                className="text-emerald-700 font-medium hover:underline" data-testid="register-goto-login">
                Accedi
              </button>
            </p>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={submitOtp} className="space-y-3" data-testid="register-otp-form">
            {debugCode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900" data-testid="register-otp-debug">
                <strong>TEST_MODE:</strong> codice <code className="font-mono">{debugCode}</code>
              </div>
            )}
            <input type="text" inputMode="numeric" value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required maxLength={6}
              className="w-full px-3 py-3 border border-zinc-300 rounded-lg text-2xl font-mono tracking-widest text-center"
              placeholder="000000" data-testid="register-otp-code" />
            {error && <p className="text-xs text-rose-600" data-testid="register-otp-error">{error}</p>}
            <button type="submit" disabled={loading || otpCode.length !== 6}
              className="w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="register-otp-submit">
              {loading ? "..." : "Conferma e accedi"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
